import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Avatar,
  Alert,
  Skeleton,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import {
  getAchievementPlatforms,
  getPSNTrophies,
  getSteamAchievements
} from '../services/api';

// Steam CDN アイコンURL構築
const buildSteamIconUrl = (steamId: number, hash: string) =>
  `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/apps/${steamId}/${hash}.jpg`;

// Steam実績インターフェース
interface SteamAchievement {
  platform: string;
  achievement_id: string;
  name: string;
  description: string;
  type: null;
  icon_hash: string | null;
  is_hidden: boolean;
  earned_rate: number;
  group_name: null;
}

interface SteamAchievementData {
  game_id: number;
  platform: string;
  has_data: boolean;
  steam_id: number;
  total_achievements: number;
  achievements: SteamAchievement[];
}

interface PSNTrophy {
  platform: string;
  achievement_id: string;
  name: string;
  description: string;
  type: string;
  icon_url: string | null;
  is_hidden: boolean;
  earned_rate: number;
  group_name: string | null;
}

interface PSNTrophyGroup {
  id: number;
  group_id: string;
  group_name: string;
  group_detail: string;
  group_icon_url: string | null;
  defined_trophies_bronze: number;
  defined_trophies_silver: number;
  defined_trophies_gold: number;
  defined_trophies_platinum: number;
}

interface PSNTrophyData {
  game_id: number;
  platform: string;
  has_data: boolean;
  trophy_title: {
    id: number;
    title_name: string;
    title_detail: string;
    defined_trophies_bronze: number;
    defined_trophies_silver: number;
    defined_trophies_gold: number;
    defined_trophies_platinum: number;
  } | null;
  trophy_groups: PSNTrophyGroup[];
  trophies: PSNTrophy[];
}

interface Platform {
  platform: string;
  name: string;
  has_data: boolean;
}

interface GameAchievementSectionProps {
  gameId: number;
}

const GameAchievementSection: React.FC<GameAchievementSectionProps> = ({ gameId }) => {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformTab, setPlatformTab] = useState<'psn' | 'steam'>('psn');
  const [psnTrophies, setPsnTrophies] = useState<PSNTrophyData | null>(null);
  const [steamAchievements, setSteamAchievements] = useState<SteamAchievementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllHidden, setShowAllHidden] = useState(false);
  const [revealedHidden, setRevealedHidden] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const response = await getAchievementPlatforms(gameId);
        setPlatforms(response.platforms);
      } catch (err) {
        console.error('プラットフォーム取得エラー:', err);
        setError('実績データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, [gameId]);

  useEffect(() => {
    const fetchAchievements = async () => {
      setLoading(true);
      try {
        if (platformTab === 'psn') {
          const trophyData = await getPSNTrophies(gameId, true);
          setPsnTrophies(trophyData);
        } else if (platformTab === 'steam') {
          const achievementData = await getSteamAchievements(gameId, true);
          setSteamAchievements(achievementData);
        }
      } catch (err) {
        console.error('実績取得エラー:', err);
        setError('実績データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchAchievements();
  }, [gameId, platformTab]);

  // プラチナグラデーション用のSVG定義コンポーネント
  const PlatinumGradientDef = () => (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <linearGradient id="platinum-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e0c3fc" />
          <stop offset="100%" stopColor="#8ec5fc" />
        </linearGradient>
      </defs>
    </svg>
  );

  const renderTrophyCard = (trophy: PSNTrophy) => {
    const isHiddenAndNotRevealed = trophy.is_hidden && !showAllHidden && !revealedHidden.has(trophy.achievement_id);

    const handleReveal = () => {
      if (trophy.is_hidden && !showAllHidden) {
        setRevealedHidden(prev => new Set(prev).add(trophy.achievement_id));
      }
    };

    const getTrophyTypeColor = (type: string) => {
      switch (type) {
        case 'gold': return '#FFD700';
        case 'silver': return '#C0C0C0';
        case 'bronze': return '#CD7F32';
        default: return '#888';
      }
    };

    return (
      <Box
        key={trophy.achievement_id}
        onClick={isHiddenAndNotRevealed ? handleReveal : undefined}
        sx={{
          mb: 0.5,
          py: 0.75,
          px: 1,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          transition: 'background-color 0.2s ease',
          cursor: isHiddenAndNotRevealed ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: isHiddenAndNotRevealed ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.04)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexShrink: 0 }}>
            {isHiddenAndNotRevealed ? (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.5,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LockIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.4)' }} />
              </Box>
            ) : trophy.icon_url ? (
              <Avatar
                src={trophy.icon_url}
                alt={trophy.name}
                variant="square"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 0.5
                }}
              />
            ) : (
              <EmojiEventsIcon
                sx={{
                  width: 28,
                  height: 28,
                  ...(trophy.type === 'platinum'
                    ? { fill: 'url(#platinum-gradient)' }
                    : { color: getTrophyTypeColor(trophy.type) }
                  )
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isHiddenAndNotRevealed ? (
              <Typography
              variant='caption'
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)'
                }}
              >
                隠しトロフィー（タップで表示）
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmojiEventsIcon
                    sx={{
                      fontSize: 15,
                      ...(trophy.type === 'platinum'
                        ? { fill: 'url(#platinum-gradient)' }
                        : { color: getTrophyTypeColor(trophy.type) }
                      )
                    }}
                  />
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: 1.2
                    }}
                  >
                    {trophy.name}
                  </Typography>
                  {trophy.is_hidden && (
                    <LockIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.3)' }} />
                  )}
                </Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'rgba(255, 255, 255, 0.55)',
                    lineHeight: 1.2,
                    mt: 0.25
                  }}
                >
                  {trophy.description}
                  {trophy.earned_rate > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: '0.6rem',
                        ml: 0.75
                      }}
                    >
                      ({trophy.earned_rate.toFixed(1)}%)
                    </Typography>
                  )}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // 汎用実績カード表示関数は削除（現在はPSNのみ対応）

  const renderPSNTrophies = () => {
    if (!psnTrophies || !psnTrophies.has_data || !psnTrophies.trophy_title) return null;

    const { trophy_title, trophy_groups, trophies } = psnTrophies;

    return (
      <Box>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <img
              src="/icons/service/playstation.svg"
              alt="PlayStation"
              style={{
                width: 16,
                height: 16,
                filter: 'brightness(0) saturate(100%) invert(31%) sepia(96%) saturate(1280%) hue-rotate(204deg) brightness(95%) contrast(101%)'
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              PlayStation Network
            </Typography>
          </Box>
          {trophy_title.title_detail && (
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.7rem',
                lineHeight: 1.3
              }}
            >
              {trophy_title.title_detail}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEventsIcon sx={{ fill: 'url(#platinum-gradient)', fontSize: '1rem' }} />
              <Typography
                sx={{
                  background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                {trophy_title.defined_trophies_platinum}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: '1rem' }} />
              <Typography
                sx={{
                  color: '#FFD700',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                {trophy_title.defined_trophies_gold}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEventsIcon sx={{ color: '#C0C0C0', fontSize: '1rem' }} />
              <Typography
                sx={{
                  color: '#C0C0C0',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                {trophy_title.defined_trophies_silver}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEventsIcon sx={{ color: '#CD7F32', fontSize: '1rem' }} />
              <Typography
                sx={{
                  color: '#CD7F32',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                {trophy_title.defined_trophies_bronze}
              </Typography>
            </Box>
          </Box>
        </Box>

        {trophy_groups.length > 0 ? (
          trophy_groups.map((group, index) => (
            <Accordion
              key={group.id}
              defaultExpanded={index === 0}
              sx={{
                mb: 1,
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.06)',
                '&:before': {
                  display: 'none'
                },
                '&.Mui-expanded': {
                  margin: '0 0 8px 0'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1.1rem' }} />}
                sx={{
                  minHeight: 36,
                  '& .MuiAccordionSummary-content': {
                    margin: '6px 0'
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  flexWrap: 'wrap'
                }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    {group.group_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {group.defined_trophies_platinum > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <EmojiEventsIcon sx={{ fill: 'url(#platinum-gradient)', fontSize: '0.7rem' }} />
                        <Typography
                          sx={{
                            background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: '0.6rem',
                            fontWeight: 500
                          }}
                        >
                          {group.defined_trophies_platinum}
                        </Typography>
                      </Box>
                    )}
                    {group.defined_trophies_gold > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: '0.7rem' }} />
                        <Typography sx={{ color: '#FFD700', fontSize: '0.6rem', fontWeight: 500 }}>
                          {group.defined_trophies_gold}
                        </Typography>
                      </Box>
                    )}
                    {group.defined_trophies_silver > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <EmojiEventsIcon sx={{ color: '#C0C0C0', fontSize: '0.7rem' }} />
                        <Typography sx={{ color: '#C0C0C0', fontSize: '0.6rem', fontWeight: 500 }}>
                          {group.defined_trophies_silver}
                        </Typography>
                      </Box>
                    )}
                    {group.defined_trophies_bronze > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <EmojiEventsIcon sx={{ color: '#CD7F32', fontSize: '0.7rem' }} />
                        <Typography sx={{ color: '#CD7F32', fontSize: '0.6rem', fontWeight: 500 }}>
                          {group.defined_trophies_bronze}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
                {group.group_detail && (
                  <Typography
                    variant='body2'
                    sx={{
                      mb: 1,
                      color: 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    {group.group_detail}
                  </Typography>
                )}
                <Box>
                  {trophies.filter(trophy => trophy.group_name === group.group_name).map(renderTrophyCard)}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box>
            {trophies.map(renderTrophyCard)}
          </Box>
        )}
      </Box>
    );
  };

  const renderSteamAchievementCard = (achievement: SteamAchievement) => {
    const isHiddenAndNotRevealed = achievement.is_hidden && !showAllHidden && !revealedHidden.has(achievement.achievement_id);

    const handleReveal = () => {
      if (achievement.is_hidden && !showAllHidden) {
        setRevealedHidden(prev => new Set(prev).add(achievement.achievement_id));
      }
    };

    return (
      <Box
        key={achievement.achievement_id}
        onClick={isHiddenAndNotRevealed ? handleReveal : undefined}
        sx={{
          mb: 0.5,
          py: 0.75,
          px: 1,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          transition: 'background-color 0.2s ease',
          cursor: isHiddenAndNotRevealed ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: isHiddenAndNotRevealed ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.04)'
          }
        }}
      >
        {/* 達成率バー（背景） */}
        {achievement.earned_rate > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${achievement.earned_rate}%`,
              bgcolor: isHiddenAndNotRevealed ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.07)',
              transition: 'width 0.3s ease',
              pointerEvents: 'none'
            }}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
          <Box sx={{ flexShrink: 0 }}>
            {isHiddenAndNotRevealed ? (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.5,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LockIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.4)' }} />
              </Box>
            ) : achievement.icon_hash && steamAchievements?.steam_id ? (
              <Avatar
                src={buildSteamIconUrl(steamAchievements.steam_id, achievement.icon_hash)}
                alt={achievement.name}
                variant="square"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 0.5
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 0.5,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 24, color: '#66c0f4' }} />
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isHiddenAndNotRevealed ? (
              <Typography
                variant='caption'
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)'
                }}
              >
                隠し実績（タップで表示）
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: 1.2
                    }}
                  >
                    {achievement.name}
                  </Typography>
                  {achievement.is_hidden && (
                    <LockIcon sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.3)' }} />
                  )}
                </Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'rgba(255, 255, 255, 0.55)',
                    lineHeight: 1.2,
                    mt: 0.25
                  }}
                >
                  {achievement.description}
                  {achievement.earned_rate > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: '0.6rem',
                        ml: 0.75
                      }}
                    >
                      ({achievement.earned_rate.toFixed(1)}%)
                    </Typography>
                  )}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSteamAchievements = () => {
    if (!steamAchievements || !steamAchievements.has_data) {
      return (
        <Alert
          severity="info"
          sx={{
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            border: '1px solid rgba(33, 150, 243, 0.2)',
            '& .MuiAlert-icon': {
              color: '#2196f3'
            }
          }}
        >
          Steam実績データがありません。
        </Alert>
      );
    }

    const { achievements, total_achievements } = steamAchievements;

    return (
      <Box>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <img
              src="/icons/service/steam_black.svg"
              alt="Steam"
              style={{
                width: 16,
                height: 16,
                filter: 'brightness(0) invert(1)'
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              Steam
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEventsIcon sx={{ color: '#66c0f4', fontSize: '1.2rem' }} />
            <Typography
              sx={{
                color: '#66c0f4',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              {total_achievements} 実績
            </Typography>
          </Box>
        </Box>

        <Box>
          {achievements.map(renderSteamAchievementCard)}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 } }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={60}
          sx={{ 
            mb: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2
          }} 
        />
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={200} 
          sx={{ 
            mb: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2
          }} 
        />
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={200} 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2
          }} 
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 } }}>
        <Alert
          severity="error" 
          sx={{ 
            bgcolor: 'rgba(244, 67, 54, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            border: '1px solid rgba(244, 67, 54, 0.2)',
            '& .MuiAlert-icon': {
              color: '#f44336'
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (platforms.length === 0) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 } }}>
        <Alert
          severity="info" 
          sx={{ 
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            color: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            border: '1px solid rgba(33, 150, 243, 0.2)',
            '& .MuiAlert-icon': {
              color: '#2196f3'
            }
          }}
        >
          このゲームには実績データがありません。
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 3, sm: 3 } }}>
      <PlatinumGradientDef />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
          実績・トロフィー
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showAllHidden}
              onChange={(e) => setShowAllHidden(e.target.checked)}
              size="small"
              sx={{
                transform: 'scale(0.8)',
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: '#1976d2'
                }
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: 'rgba(255, 255, 255, 0.5)'
              }}
            >
              隠し実績を表示
            </Typography>
          }
          sx={{ mr: 0 }}
        />
      </Box>

      {/* プラットフォームタブ */}
      <Tabs
        value={platformTab}
        onChange={(e, value) => setPlatformTab(value)}
        sx={{
          mb: 1.5,
          minHeight: 32,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '0.7rem',
            fontWeight: 500,
            minHeight: 32,
            py: 0.5,
            px: 1.5,
            color: 'rgba(255, 255, 255, 0.4)',
            '&.Mui-selected': {
              color: 'rgba(255, 255, 255, 0.9)',
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
            height: '2px',
          }
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <img
                src="/icons/service/playstation.svg"
                alt="PlayStation"
                style={{
                  width: 14,
                  height: 14,
                  filter: platformTab === 'psn'
                    ? 'brightness(0) invert(1)'
                    : 'brightness(0) invert(0.4)'
                }}
              />
              PSN
            </Box>
          }
          value="psn"
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <img
                src="/icons/service/steam_black.svg"
                alt="Steam"
                style={{
                  width: 14,
                  height: 14,
                  filter: platformTab === 'steam'
                    ? 'brightness(0) invert(1)'
                    : 'brightness(0) invert(0.4)'
                }}
              />
              Steam
            </Box>
          }
          value="steam"
        />
      </Tabs>

      {platformTab === 'psn' && renderPSNTrophies()}

      {platformTab === 'steam' && renderSteamAchievements()}
    </Box>
  );
};

export default GameAchievementSection; 
